import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Select, Table, Spin } from 'antd';
import { useDispatch } from 'react-redux';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    CartesianGrid,
    LineChart,
    Line,
    ComposedChart,
    AreaChart,
    Area,
    RadialBarChart,
    RadialBar,
} from 'recharts';
import { getData } from '../../../../actions/apiCall';
import BdeStatsDashboard from '../BdeStatsDashboard';
import moment from 'moment';
import RenderFormItem from '../../../../components/Forms/FormItems';
import { constants } from '../../../../constants/common';
import SmallProfileCard from '../../../../components/ContactInfo/SmallProfileCard';
import { useSelector } from 'react-redux';

const { Title } = Typography;

const BdeDashboard: React.FC = () => {
    const [period, setPeriod] = useState('quarterly');
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [bdeId, setBDE] = useState(null);
    const { user } = useSelector((state: any) => state.app);
    const dispatch = useDispatch();

    useEffect(() => {
        fetchDashboard();
    }, [period, bdeId]);

    const fetchDashboard = async () => {
        try {
            setLoading(true);
            const allBDES = bdeId === 'all';
            const resData: any = await dispatch(getData(`/targets?period=${period}&bdeId=${bdeId}&allBDEs=${allBDES}`));
            if (resData?.code === 200) {
                setStats(resData.data);
            }
        } catch (error) {
            console.error('Error fetching BDE Dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    // Table columns
    const columns = [
        { title: 'Metric', dataIndex: 'metric', key: 'metric' },
        { title: 'Target', dataIndex: 'target', key: 'target' },
        { title: 'Achieved', dataIndex: 'achieved', key: 'achieved' },
        { title: 'Remaining', dataIndex: 'remaining', key: 'remaining' },
        { title: 'Percentage', dataIndex: 'percentage', key: 'percentage', render: (val: number) => `${val}%` },
    ];

    const tableData = stats
        ? [
            {
                key: '1',
                metric: 'Revenue',
                ...stats.targets.revenue,
            },
            {
                key: '2',
                metric: 'Deals',
                ...stats.targets.deals,
            },
            {
                key: '3',
                metric: 'Accounts',
                ...stats.targets.accounts,
            },
        ]
        : [];

    // Recharts data
    const chartData = stats
        ? [
            { name: 'Revenue', Target: stats.targets.revenue.target, Achieved: stats.targets.revenue.achieved },
            { name: 'Deals', Target: stats.targets.deals.target, Achieved: stats.targets.deals.achieved },
            { name: 'Accounts', Target: stats.targets.accounts.target, Achieved: stats.targets.accounts.achieved },
        ]
        : [];

    return (
        <div className="p-4 min-h-screen">
            <Row justify="space-between" align="middle" className="mb-4">
                <Title level={5}>My Targets</Title>
                <div className='flex items-center gap-2'>
                    {user?.designation === 'Admin' && <RenderFormItem
                        column={{
                            label: '',
                            name: 'bde',
                            type: 'select',
                            isCombo: true,
                            span: 24,
                            selectProps: {
                                showSearch: true,
                                allowClear: true,
                                placeholder: 'Select BDE to get data.',
                                style: { width: 200 },
                                onChange: (value) => {
                                    setBDE(value);
                                }
                            },
                            extraOptions: [
                                {
                                    name: 'All',
                                    value: 'all',
                                }
                            ]
                        }}
                        combo={[
                            {
                                name: 'bde',
                                api: '/users',
                                idKey: '_id',
                                render: (text) => <SmallProfileCard
                                    name={constants.GET_FULL_NAME(text)}
                                    avatar={text?.avatar}
                                />
                            }
                        ]}
                    />}
                    <Select
                        value={period}
                        onChange={setPeriod}
                        options={[
                            { label: 'Weekly', value: 'weekly' },
                            { label: 'Monthly', value: 'monthly' },
                            { label: 'Quarterly', value: 'quarterly' },
                            { label: 'Yearly', value: 'yearly' },
                        ]}
                        style={{ width: 150 }}
                    />
                </div>
            </Row>

            {loading ? (
                <Spin size="large" className="w-full flex justify-center items-center min-h-[200px]" />
            ) : stats ? (
                <>
                    {/* Stats Section */}
                    <Row gutter={[16, 16]} className="mb-6">
                        <Col xs={24} md={8}>
                            <Card
                                title={
                                    <div className='flex justify-between items-center'>
                                        <span className="text-lg font-semibold">
                                            Revenue
                                        </span>
                                        <p className="text-sm text-gray-500 font-regular">
                                            <span>{stats.period.toUpperCase()}</span> ({moment(stats.startDate).format('MMM D, YYYY')} - {moment(stats.endDate).format('MMM D, YYYY')})
                                        </p>
                                    </div>
                                } className="mb-4"
                            >
                                <BdeStatsDashboard stats={stats} only="revenue" />
                            </Card>
                        </Col>
                        <Col xs={24} md={8}>
                            <Card
                                title={
                                    <div className='flex justify-between items-center'>
                                        <span className="text-lg font-semibold">
                                            Deals
                                        </span>
                                        <p className="text-sm text-gray-500 font-regular">
                                            <span>{stats.period.toUpperCase()}</span> ({moment(stats.startDate).format('MMM D, YYYY')} - {moment(stats.endDate).format('MMM D, YYYY')})
                                        </p>
                                    </div>
                                }
                            >
                                <BdeStatsDashboard stats={stats} only="deals" />
                            </Card>
                        </Col>
                        <Col xs={24} md={8}>
                            <Card
                                title={
                                    <div className='flex justify-between items-center'>
                                        <span className="text-lg font-semibold">
                                            Accounts
                                        </span>
                                        <p className="text-sm text-gray-500 font-regular">
                                            <span>{stats.period.toUpperCase()}</span> ({moment(stats.startDate).format('MMM D, YYYY')} - {moment(stats.endDate).format('MMM D, YYYY')})
                                        </p>
                                    </div>
                                }
                            >
                                <BdeStatsDashboard stats={stats} only="accounts" />
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]} className="mb-6">
                        <Col xs={24} md={8}>
                            <Card title="Revenue Trend">
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={stats?.lineGraph}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="revenue" stroke="#8884d8" strokeWidth={2} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col xs={24} md={8}>
                            <Card title="Deals Trend">
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={stats?.lineGraph}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="deals" stroke="#82ca9d" strokeWidth={2} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col xs={24} md={8}>
                            <Card title="Accounts Trend">
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={stats?.lineGraph}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="accounts" stroke="#faad14" strokeWidth={2} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col span={24}>
                            <Card title="Performance Line Chart">
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={stats.lineGraph}>
                                        <defs>
                                            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                                                <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" tickFormatter={(value) => moment(value).format('DD MMM')} />
                                        <YAxis />
                                        <Tooltip />
                                        <Area type="monotone" dataKey="revenue" stroke="#8884d8" fillOpacity={1} fill="url(#colorRevenue)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]} className="mb-6">
                        <Col xs={12}>
                            <Card title="Target vs Achieved" className="mb-6">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="Target" fill="#8884d8" />
                                        <Bar dataKey="Achieved" fill="#82ca9d" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                        <Col xs={12}>
                            <Card title="Performance Breakdown">
                                <Table columns={columns} dataSource={tableData} pagination={false} />
                            </Card>
                        </Col>
                    </Row>


                </>
            ) : (
                <p>No data available</p>
            )}
        </div>
    );
};

export default BdeDashboard;
